import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, MessageSquare, Calendar, TrendingUp, CheckCircle, X, Settings } from 'lucide-react';

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const notifications = [
  {
    id: 1,
    icon: MessageSquare,
    title: 'New Comment',
    message: 'Sarah left a comment on your post',
    time: '2 minutes ago',
    color: 'text-blue-500 bg-blue-100 dark:bg-blue-900/30',
    unread: true
  },
  {
    id: 2,
    icon: TrendingUp,
    title: 'Analytics Update',
    message: 'Your post is trending with 500+ views',
    time: '1 hour ago',
    color: 'text-green-500 bg-green-100 dark:bg-green-900/30',
    unread: true
  },
  {
    id: 3,
    icon: Calendar,
    title: 'Scheduled Post',
    message: 'Your post is scheduled for tomorrow',
    time: '3 hours ago',
    color: 'text-purple-500 bg-purple-100 dark:bg-purple-900/30',
    unread: false
  },
  {
    id: 4,
    icon: Bell,
    title: 'Content Reminder',
    message: 'Time to create your weekly content',
    time: '1 day ago',
    color: 'text-amber-500 bg-amber-100 dark:bg-amber-900/30',
    unread: false
  },
  {
    id: 5,
    icon: CheckCircle,
    title: 'Task Completed',
    message: 'Your scheduled post was published',
    time: '2 days ago',
    color: 'text-indigo-500 bg-indigo-100 dark:bg-indigo-900/30',
    unread: false
  }
];

export const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({ isOpen, onClose }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="fixed top-16 right-0 sm:right-6 sm:top-20 w-full sm:w-80 rounded-lg shadow-lg bg-white dark:bg-dark-800 ring-1 ring-black ring-opacity-5 z-50 border border-gray-100 dark:border-dark-700 max-h-[80vh] flex flex-col max-w-[100vw] sm:max-w-[320px]"
          >
            <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notifications</h3>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-indigo-100 bg-indigo-600 rounded-full">
                    {notifications.filter(n => n.unread).length}
                  </span>
                  <button 
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto flex-grow">
              {notifications.map((notification) => {
                const Icon = notification.icon;
                return (
                  <motion.div
                    key={notification.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    whileHover={{ scale: 1.02, backgroundColor: 'rgba(0,0,0,0.02)' }}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer border-l-2 ${
                      notification.unread 
                        ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10' 
                        : 'border-transparent'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className={`p-2 rounded-full ${notification.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {notification.title}
                          </p>
                          {notification.unread && (
                            <span className="inline-block h-2 w-2 rounded-full bg-indigo-500"></span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {notification.time}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-dark-700 flex items-center justify-between flex-shrink-0">
              <button
                className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-500 dark:hover:text-indigo-300 font-medium"
                onClick={() => {/* Handle mark all as read */}}
              >
                Mark all as read
              </button>
              <button
                className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 flex items-center"
                onClick={() => {/* Handle notification settings */}}
              >
                <Settings className="h-3.5 w-3.5 mr-1" />
                Settings
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};